import { AssessmentSolutionWithSolutions } from "@appally/collections/AssessmentSolution";
import { AssessmentStats } from "@appally/collections/AssessmentStats";
import { ManagerAssessments } from "@appally/collections/ManagerAssessments";
import { Organisation } from "@appally/collections/Organisation";
import { OrganisationSolution } from "@appally/collections/OrganisationSolution";
import { Solution } from "@appally/collections/Solution";
import { User } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { combineAssessmentSolutionWithSolutions } from "../actions/assessment";
import { getCurrentUserAsOrgAdmin } from "../actions/user";

export type UpdateOrganisationSolutionPayload = {
  organisationId: string;
  solutionId: string;
  changes: Partial<OrganisationSolution["solutions"][Solution["id"]]>;
};

// Define a service using a base URL and expected endpoints
export const organisationApi = createApi({
  reducerPath: "organisation",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "Organisation",
    "AssessmentSolution",
    "User",
    "ManagerAssessments",
    "OrganisationSolution",
    "Solution",
  ],
  endpoints: (builder) => ({
    // Get all the assessments that this user has access to
    getOrganisation: builder.query<Organisation | null, string>({
      queryFn: async (id) => {
        // Get the current user
        const currentUser = await getCurrentUserAsOrgAdmin(id);
        if (!currentUser) return { data: null };

        // Get the organisation
        const org = await getDoc(doc(collections.organisations, id));

        return { data: org.exists() ? org.data() : null };
      },
      providesTags: (result) => [{ type: "Organisation", id: result?.id }],
    }),

    // Get the full adjustment catalogue
    getSolutions: builder.query<Solution[], string>({
      queryFn: async (orgId) => {
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) return { data: [] };

        const thisDoc = await getDocs(query(collections.solutions));
        const data = thisDoc.docs.map((d) => d.data());
        return { data };
      },
      providesTags: ["Solution"],
    }),

    // Get the organisation-specific settings for the adjustment catalogue
    getOrganisationSolutions: builder.query<OrganisationSolution, string>({
      queryFn: async (orgId) => {
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) {
          return {
            data: {
              id: orgId,
              solutions: {},
            },
          };
        }

        const thisDoc = await getDoc(
          doc(collections.organisation_solutions, orgId),
        );
        return {
          data: thisDoc.exists()
            ? thisDoc.data()
            : {
                id: orgId,
                solutions: {},
              },
        };
      },
      providesTags: (result, error, orgId) => [
        { type: "OrganisationSolution", id: orgId },
      ],
    }),

    // Update one solution's organisation-specific settings
    updateOrganisationSolution: builder.mutation<
      boolean,
      UpdateOrganisationSolutionPayload
    >({
      queryFn: async (input) => {
        const currentUser = await getCurrentUserAsOrgAdmin(
          input.organisationId,
        );
        if (!currentUser) {
          throw new Error(
            "You don't have access to update this organisation's adjustments",
          );
        }

        const thisDoc = await getDoc(
          doc(collections.organisation_solutions, input.organisationId),
        );
        const organisationSolution = thisDoc.exists()
          ? thisDoc.data()
          : {
              id: input.organisationId,
              solutions: {},
            };
        const currentSolution =
          organisationSolution.solutions[input.solutionId] ?? {};

        await setDoc(
          doc(collections.organisation_solutions, input.organisationId),
          {
            solutions: {
              ...organisationSolution.solutions,
              [input.solutionId]: {
                ...currentSolution,
                ...input.changes,
              },
            },
          },
          { merge: true },
        );

        return { data: true };
      },
      invalidatesTags: (result, error, input) => [
        { type: "OrganisationSolution", id: input.organisationId },
      ],
    }),

    // Get all the assessment stats in the organisation
    getOrganisationAssessmentStats: builder.query<AssessmentStats[], string>({
      queryFn: async (orgId) => {
        // Get the current user
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) return { data: [] };

        // Get the solutions we're allowed to
        const thisDoc = await getDocs(
          query(
            collections.assessment_stats,
            where("organisation_id", "==", orgId),
          ),
        );
        const data = thisDoc.docs.map((d) => d.data());
        return { data };
      },
      providesTags: ["AssessmentSolution"],
    }),

    // Get all the assessment solutions in the organisation
    getOrganisationAssessmentSolutions: builder.query<
      AssessmentSolutionWithSolutions[],
      string
    >({
      queryFn: async (orgId) => {
        // Get the current user
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) return { data: [] };

        // Get the solutions we're allowed to
        const thisDoc = await getDocs(
          query(
            collections.assessment_solutions,
            where("organisation_id", "==", orgId),
          ),
        );
        if (!thisDoc.docs.length) return { data: [] };
        const resolved = await Promise.all(
          thisDoc.docs.map((d) => {
            return combineAssessmentSolutionWithSolutions(d.data());
          }),
        );
        const data = resolved.flat();

        return { data };
      },
      providesTags: ["AssessmentSolution"],
    }),

    // Get all the users in the organisation
    getOrganisationUsers: builder.query<User[], string>({
      queryFn: async (orgId) => {
        // Get the current user
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) return { data: [] };

        // Get the users we're allowed to
        const thisDoc = await getDocs(
          query(collections.users, where("organisation_id", "==", orgId)),
        );
        if (!thisDoc.docs.length) return { data: [] };
        const data = thisDoc.docs.map((d) => d.data());

        return { data };
      },
      providesTags: ["User"],
    }),

    // Get all the assessments that this user has access to
    getOrganisationManagerAssessments: builder.query<
      (ManagerAssessments & { id: string })[],
      string
    >({
      queryFn: async (orgId) => {
        // Get the current user
        const currentUser = await getCurrentUserAsOrgAdmin(orgId);
        if (!currentUser) return { data: [] };

        // Get the manager records
        const managerDocs = await getDocs(
          query(
            collections.assessment_managers,
            where("organisation_id", "==", orgId),
          ),
        );
        if (!managerDocs.docs.length) return { data: [] };
        const data = managerDocs.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));

        return {
          data,
        };
      },
      providesTags: ["ManagerAssessments"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetOrganisationQuery,
  useGetSolutionsQuery,
  useGetOrganisationSolutionsQuery,
  useUpdateOrganisationSolutionMutation,
  useGetOrganisationAssessmentStatsQuery,
  useGetOrganisationAssessmentSolutionsQuery,
  useGetOrganisationUsersQuery,
  useGetOrganisationManagerAssessmentsQuery,
} = organisationApi;
